/**
 * iter160 — Tiptap-based rich-text composer for team chat.
 *
 * Replaces the iter159 textarea. Ships bold / italic / underline /
 * bullet & ordered lists / link / hard-break / placeholder with real
 * keyboard shortcuts (Ctrl/Cmd + B/I/U) that show REAL formatting in
 * the composer — no more literal `**text**` visible while typing.
 *
 * Keyboard contract (exposed to Messages.jsx via props):
 *   Enter        → onSend(markdown) (Enter-to-send preserved)
 *   Shift+Enter  → hard break (Tiptap default, we don't override)
 *   Ctrl/Cmd+B   → toggle bold  (Tiptap default)
 *   Ctrl/Cmd+I   → toggle italic
 *   Ctrl/Cmd+U   → toggle underline
 *
 * Sizing (iter160-sizing-v3 — content-driven from 1 line up to 300px):
 *
 *   OUTER wrapper `[data-testid="team-chat-editor-root"]`:
 *     `max-height: 300px; overflow-y: auto` via inline style.
 *     NO min-height — height is fully content-driven from the inner
 *     `.ProseMirror`. Empty = one placeholder line + padding (~44px).
 *
 *   INNER `.ProseMirror`:
 *     No min-h / max-h / overflow rules. Grows naturally with content
 *     up to whatever height the browser lays it out at.
 *
 *   Combined behaviour (matches Slack / iMessage / this Emergent chat):
 *     Empty          → ~44px (single line + padding)
 *     1 line typed   → ~44px
 *     2 lines        → ~66px
 *     N lines        → ~44 + 22*(N-1) px
 *     ≥ 300px worth  → outer clamps at 300px, overflow-y:auto scrolls
 *     Delete content → shrinks line-by-line back to ~44px
 *
 * Why max-height + overflow live on the OUTER wrapper (NEVER on the
 * inner `.ProseMirror`): when a contenteditable has both `overflow-y:
 * auto` AND `max-height`, every major browser treats it as an inline
 * scroll container and holds it at its `min-height`, scrolling content
 * inside instead of growing up to `max-height`. Outer-wrapper pattern
 * matches every Tiptap-based chat composer (Notion / Slack / Linear).
 *
 * Storage: on send we call `editor.getJSON()` → docToMiniMarkdown() to
 * produce the same iter156/159 mini-Markdown string. Zero backend
 * change; existing plain-text messages keep rendering identically.
 *
 * Paste: Tiptap's built-in ProseMirror paste handling already parses
 * <b>/<i>/<u>/<ul>/<ol>/<a>/<p> from Google Docs / Word / Outlook
 * because those extensions register `parseHTML` rules. Scripts, event
 * handlers, and unsupported HTML never enter the editor state — ProseMirror
 * only accepts nodes/marks whitelisted by loaded extensions.
 */
import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import CharacterCount from "@tiptap/extension-character-count";
import { docToMiniMarkdown, visibleTextLength } from "@/lib/tiptapToMarkdown";

const MAX_CHARS = 4000;

const TeamChatEditor = forwardRef(function TeamChatEditor(
  { onSubmit, onChange, disabled = false, placeholder = "Type a message  (Enter to send · Shift+Enter for a new line)" },
  ref,
) {
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        // We use our own Link (with auto-linkify + safe href).
        // Everything else (bold, italic, bulletList, orderedList,
        // hardBreak, paragraph, listItem) is from StarterKit's defaults.
        heading: { levels: [1, 2, 3] },
        link: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer nofollow",
        },
        protocols: ["http", "https", "mailto"],
      }),
      Placeholder.configure({ placeholder }),
      CharacterCount.configure({ limit: MAX_CHARS }),
    ],
    content: "",
    editorProps: {
      // Enter-to-send. Shift+Enter falls through to Tiptap's default
      // hard-break; Ctrl/Cmd+Enter is treated like Shift+Enter to avoid
      // accidental sends when muscle memory says "Cmd+Enter".
      handleKeyDown(_view, event) {
        if (event.key !== "Enter") return false;
        if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return false;
        if (event.isComposing || event.keyCode === 229) return false;
        event.preventDefault();
        editorRef.current?.submit?.();
        return true;
      },
      attributes: {
        "data-testid": "team-chat-editor",
        // Composer font matches sent-message bubble font (15px) so the
        // draft looks identical to how it will appear once sent.
        class: "focus:outline-none px-3 py-1.5 text-[15px] leading-[1.4]",
      },
    },
    onUpdate({ editor }) {
      onChange?.(editor);
    },
  });

  // Ref so `handleKeyDown` (captured at construction time) can reach the
  // latest submit handler / editor.
  const editorRef = React.useRef({});
  useEffect(() => {
    editorRef.current.submit = () => {
      if (!editor) return;
      const json = editor.getJSON();
      const body = docToMiniMarkdown(json);
      onSubmit?.(body);
    };
  }, [editor, onSubmit]);

  useImperativeHandle(ref, () => ({
    editor,
    /** Extract the current body as mini-Markdown. */
    getBody: () => (editor ? docToMiniMarkdown(editor.getJSON()) : ""),
    /** Visible character count (markup NOT counted). */
    getVisibleLength: () => (editor ? visibleTextLength(editor.getJSON()) : 0),
    /** Clear composer after send. */
    clear: () => editor?.commands.clearContent(true),
    /** Programmatic focus for the emoji picker etc. */
    focus: () => editor?.commands.focus(),
    /** Insert plain text at the cursor (used by the emoji picker). */
    insertText: (t) => editor?.chain().focus().insertContent(t).run(),
    /** Return true when the editor is empty (no visible chars, no
     *  hard-break-only "paragraph"). Used to disable Send. */
    isEmpty: () => !editor || editor.isEmpty,
    /** Toggle formatting from a toolbar (Ctrl+B/I/U already work). */
    toggleBold:      () => editor?.chain().focus().toggleBold().run(),
    toggleItalic:    () => editor?.chain().focus().toggleItalic().run(),
    toggleUnderline: () => editor?.chain().focus().toggleUnderline().run(),
  }), [editor]);

  return (
    <div
      data-testid="team-chat-editor-root"
      style={{
        maxHeight: "300px",
        overflowY: "auto",
      }}
    >
      <EditorContent editor={editor} />
    </div>
  );
});

export default TeamChatEditor;
export { MAX_CHARS };
